/**
 * IndexedDB Utility Functions
 * 
 * This file contains utilities for managing IndexedDB transactions
 * and ensuring proper index creation across tables.
 */

import { DB_CONSTANTS } from './dbStorageHelper';

/**
 * Executes a database operation within a properly managed transaction
 * @param {IDBDatabase} db - Database instance
 * @param {string} tableName - Table to operate on
 * @param {Function} operation - Function that performs the database operation
 * @param {string} mode - Transaction mode ('readonly' or 'readwrite')
 * @param {number} timeout - Timeout in milliseconds (default: a5000)
 * @returns {Promise<any>} Result of the operation
 */
export async function executeTransaction(db, tableName, operation, mode = 'readonly', timeout = 5000) {
  return new Promise((resolve, reject) => {
    if (!db) {
      return reject(new Error('Database not initialized'));
    }
    
    try {
      console.debug(`[indexedDBUtils] Starting ${mode} transaction on ${tableName}`);
      const transaction = db.transaction(tableName, mode);
      const store = transaction.objectStore(tableName);
      
      let timeoutId = null;
      let settled = false;
      let operationResult = null;
      
      // Helper to ensure we only settle the promise once
      const settleOnce = (fn) => (value) => {
        if (!settled) {
          settled = true;
          clearTimeout(timeoutId);
          console.debug(`[indexedDBUtils] Transaction for ${tableName} settled`);
          fn(value);
        }
      };
      
      // Set up transaction lifecycle handlers
      transaction.oncomplete = (event) => {
        console.debug(`[indexedDBUtils] Transaction for ${tableName} completed successfully`);
        settleOnce(resolve)(operationResult);
      };
      
      transaction.onerror = (event) => {
        console.error(`[indexedDBUtils] Transaction error for ${tableName}:`, event.target.error);
        settleOnce(reject)(event.target.error);
      };
      
      transaction.onabort = (event) => {
        console.warn(`[indexedDBUtils] Transaction aborted for ${tableName}`);
        settleOnce(reject)(new Error('Transaction aborted'));
      };
      
      // Execute the operation and track its result
      try {
        const result = operation(store, transaction);
        
        // If operation returns a promise, chain our settlement to it
        if (result instanceof Promise) {
          result
            .then(value => {
              operationResult = value;
              // Don't resolve here - wait for transaction.oncomplete
            })
            .catch(error => {
              console.error(`[indexedDBUtils] Operation error in ${tableName}:`, error);
              settleOnce(reject)(error);
            });
        } else {
          // Store the result for later resolution
          operationResult = result;
        }
      } catch (error) {
        console.error(`[indexedDBUtils] Error executing operation on ${tableName}:`, error);
        return settleOnce(reject)(error);
      }
      
      // Set timeout to abort hanging transactions
      timeoutId = setTimeout(() => {
        if (!settled) {
          console.warn(`[indexedDBUtils] Transaction timeout for ${tableName} after ${timeout}ms, aborting`);
          try {
            transaction.abort();
            settleOnce(reject)(new Error(`Transaction timeout for ${tableName} after ${timeout}ms`));
          } catch (e) {
            console.error(`[indexedDBUtils] Error aborting transaction:`, e);
            settleOnce(reject)(new Error(`Failed to abort timed out transaction: ${e.message}`));
          }
        }
      }, timeout);
    } catch (error) {
      console.error(`[indexedDBUtils] Error creating transaction for ${tableName}:`, error);
      reject(error);
    }
  });
}

/**
 * Verifies if a table has all the required indexes
 * @param {IDBDatabase} db - Database instance
 * @param {string} tableName - Table to check
 * @returns {Promise<Object>} - Information about table indexes
 */
export async function verifyTableIndexes(db, tableName) {
  if (!db) {
    throw new Error('Database not initialized');
  }
  
  if (!db.objectStoreNames.contains(tableName)) {
    console.warn(`[indexedDBUtils] Table ${tableName} doesn't exist`);
    return { exists: false, indexes: [], missingIndexes: [] };
  }
  
  return executeTransaction(db, tableName, (store) => {
    // Critical indexes that should be present on all tables
    const requiredIndexes = [
      { name: 'uuid', unique: true },
      { name: 'lastModified', unique: false },
      { name: 'isDeleted', unique: false }
    ];
    
    const existingIndexes = Array.from(store.indexNames);
    const missingIndexes = requiredIndexes.filter(idx => 
      !existingIndexes.includes(idx.name)
    );
    
    return {
      exists: true,
      indexes: existingIndexes,
      missingIndexes: missingIndexes.map(idx => idx.name),
      hasAllRequired: missingIndexes.length === 0
    };
  });
}

/**
 * Ensures all required indexes exist on a table
 * Note: This requires a database version upgrade to add indexes
 * @param {string} dbName - Database name
 * @param {string[]} tables - Tables to check/upgrade
 * @returns {Promise<boolean>} - Whether any indexes were added
 */
export async function ensureCriticalIndexes(dbName, tables) {
  if (!dbName || !tables || !tables.length) {
    return false;
  }
  
  // First open the DB to check current indexes and version
  const openRequest = indexedDB.open(dbName);
  
  try {
    const db = await new Promise((resolve, reject) => {
      openRequest.onsuccess = () => resolve(openRequest.result);
      openRequest.onerror = () => reject(openRequest.error);
    });
    
    // Check which tables are missing indexes
    const tablesInfo = [];
    for (const tableName of tables) {
      if (db.objectStoreNames.contains(tableName)) {
        const result = await verifyTableIndexes(db, tableName);
        if (result.missingIndexes.length > 0) {
          tablesInfo.push({
            name: tableName,
            missingIndexes: result.missingIndexes
          });
        }
      }
    }
    
    // Close the current database connection
    db.close();
    
    // If there are missing indexes, upgrade the database
    if (tablesInfo.length > 0) {
      // console.log(`[indexedDBUtils] Tables requiring index upgrades:`, tablesInfo);
      
      // Increment version and reopen for upgrade
      const currentVersion = db.version;
      const newVersion = currentVersion + 1;
      
      const upgradeRequest = indexedDB.open(dbName, newVersion);
      
      return new Promise((resolve, reject) => {
        upgradeRequest.onupgradeneeded = (event) => {
          const db = event.target.result;
          
          for (const tableInfo of tablesInfo) {
            if (db.objectStoreNames.contains(tableInfo.name)) {
              const store = event.target.transaction.objectStore(tableInfo.name);
              
              // Add the missing indexes
              for (const indexName of tableInfo.missingIndexes) {
                // Apply index with appropriate settings
                if (indexName === 'uuid') {
                  // console.log(`[indexedDBUtils] Adding missing uuid index to ${tableInfo.name}`);
                  if (!store.indexNames.contains(indexName)) {
                    store.createIndex(indexName, indexName, { unique: true });
                  }
                } else if (indexName === 'lastModified') {
                  // console.log(`[indexedDBUtils] Adding missing lastModified index to ${tableInfo.name}`);
                  if (!store.indexNames.contains(indexName)) {
                    store.createIndex(indexName, indexName, { unique: false });
                  }
                } else if (indexName === 'isDeleted') {
                  // console.log(`[indexedDBUtils] Adding missing isDeleted index to ${tableInfo.name}`);
                  if (!store.indexNames.contains(indexName)) {
                    store.createIndex(indexName, indexName, { unique: false });
                  }
                }
              }
            }
          }
        };
        
        upgradeRequest.onsuccess = () => {
          // console.log(`[indexedDBUtils] Successfully upgraded database to version ${newVersion}`);
          const db = upgradeRequest.result;
          db.close();
          resolve(true);
        };
        
        upgradeRequest.onerror = () => {
          console.error(`[indexedDBUtils] Error upgrading database:`, upgradeRequest.error);
          reject(upgradeRequest.error);
        };
      });
    }
    
    return false; // No indexes needed to be added
  } catch (error) {
    console.error(`[indexedDBUtils] Error ensuring indexes:`, error);
    throw error;
  }
}

/**
 * Get records by boolean index efficiently
 * Special handling for boolean indexes like isDeleted
 * @param {IDBDatabase} db - Database instance
 * @param {string} tableName - Table to query
 * @param {string} indexName - Boolean index name
 * @param {boolean} value - Boolean value to filter by
 * @returns {Promise<Array>} Matching records
 */
export async function getRecordsByBooleanIndex(db, tableName, indexName, value) {
  return executeTransaction(db, tableName, (store) => {
    return new Promise((resolve) => {
      // For boolean fields, check if index exists
      if (!store.indexNames.contains(indexName)) {
        console.warn(`[indexedDBUtils] Index ${indexName} not found, falling back to filter`);
        // Fall back to getting all records and filtering
        const getRequest = store.getAll();
        getRequest.onsuccess = () => {
          const allRecords = getRequest.result;
          resolve(allRecords.filter(record => Boolean(record[indexName]) === value));
        };
        getRequest.onerror = () => resolve([]);
        return;
      }
      
      // Get all records from the index
      const index = store.index(indexName);
      const getAllRequest = index.getAll();
      
      getAllRequest.onsuccess = () => {
        // Need to manually filter since boolean indexes may behave inconsistently
        const records = getAllRequest.result;
        resolve(records.filter(record => Boolean(record[indexName]) === value));
      };
      
      getAllRequest.onerror = () => resolve([]);
    });
  });
}

// Add a new function to ensure the userId index exists on the Notification table
export const ensureNotificationUserIdIndex = async (dbName = 'elearningtbdDb') => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(dbName);
        
        request.onerror = (event) => {
            console.error('[indexedDBUtils] Error opening database to check indexes:', event.target.error);
            reject(event.target.error);
        };
        
        request.onsuccess = (event) => {
            const db = event.target.result;
            
            // Check if Notification table exists
            if (!db.objectStoreNames.contains('Notification')) {
                // console.log('[indexedDBUtils] Notification table does not exist yet, skipping index check');
                db.close();
                resolve(false);
                return;
            }
            
            // Check if we need to update the database to add the userId index
            const transaction = db.transaction('Notification', 'readonly');
            const objectStore = transaction.objectStore('Notification');
            
            if (!objectStore.indexNames.contains('userId')) {
                // console.log('[indexedDBUtils] Notification table missing userId index, will create it');
                
                // Close the current connection
                db.close();
                
                // Reopen with higher version to create the index
                const currentVersion = db.version;
                const upgradeRequest = indexedDB.open(dbName, currentVersion + 1);
                
                upgradeRequest.onupgradeneeded = (event) => {
                    const upgradeDb = event.target.result;
                    const upgradeStore = upgradeDb.objectStore('Notification');
                    
                    // Create the userId index
                    if (!upgradeStore.indexNames.contains('userId')) {
                        upgradeStore.createIndex('userId', 'userId', { unique: false });
                        // console.log('[indexedDBUtils] Created userId index on Notification table');
                    }
                };
                
                upgradeRequest.onsuccess = () => {
                    // Close the connection after upgrading
                    upgradeRequest.result.close();
                    // console.log('[indexedDBUtils] Successfully added userId index to Notification table');
                    resolve(true);
                };
                
                upgradeRequest.onerror = (event) => {
                    console.error('[indexedDBUtils] Error upgrading database:', event.target.error);
                    reject(event.target.error);
                };
            } else {
                // console.log('[indexedDBUtils] userId index already exists on Notification table');
                db.close();
                resolve(false);
            }
        };
    });
};

/**
 * Ensures all tables have the critical uuid index
 * @param {string} dbName - Database name
 * @returns {Promise<boolean>} - Whether any indexes were added
 */
export const ensureUuidIndex = async (dbName = 'elearningtbdDb') => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(dbName);
        
        request.onerror = (event) => {
            console.error('[indexedDBUtils] Error opening database to check indexes:', event.target.error);
            reject(event.target.error);
        };
        
        request.onsuccess = (event) => {
            const db = event.target.result;
            const storeNames = Array.from(db.objectStoreNames);
            const tablesNeedingUpdate = [];
            
            // For each store, check if uuid index exists
            for (const storeName of storeNames) {
                try {
                    const transaction = db.transaction(storeName, 'readonly');
                    const store = transaction.objectStore(storeName);
                    
                    if (!store.indexNames.contains('uuid')) {
                        // console.log(`[indexedDBUtils] Table ${storeName} missing uuid index`);
                        tablesNeedingUpdate.push(storeName);
                    }
                } catch (error) {
                    console.warn(`[indexedDBUtils] Error checking ${storeName} for uuid index:`, error);
                }
            }
            
            // Close this connection
            db.close();
            
            // If any tables need updating, upgrade the database
            if (tablesNeedingUpdate.length > 0) {
                // console.log(`[indexedDBUtils] Adding uuid index to ${tablesNeedingUpdate.length} tables`);
                
                // Reopen with higher version to create the indexes
                const currentVersion = db.version;
                const upgradeRequest = indexedDB.open(dbName, currentVersion + 1);
                
                upgradeRequest.onupgradeneeded = (event) => {
                    const upgradeDb = event.target.result;
                    
                    // For each table needing the index
                    for (const tableName of tablesNeedingUpdate) {
                        if (upgradeDb.objectStoreNames.contains(tableName)) {
                            const store = upgradeRequest.transaction.objectStore(tableName);
                            
                            // Create the uuid index if it doesn't exist
                            if (!store.indexNames.contains('uuid')) {
                                store.createIndex('uuid', 'uuid', { unique: true });
                                // console.log(`[indexedDBUtils] Created uuid index on ${tableName} table`);
                            }
                        }
                    }
                };
                
                upgradeRequest.onsuccess = () => {
                    upgradeRequest.result.close();
                    // console.log('[indexedDBUtils] Successfully added uuid indexes to tables');
                    resolve(true);
                };
                
                upgradeRequest.onerror = (event) => {
                    console.error('[indexedDBUtils] Error upgrading database:', event.target.error);
                    reject(event.target.error);
                };
            } else {
                // console.log('[indexedDBUtils] All tables already have uuid index');
                resolve(false);
            }
        };
    });
}; 